import { SEO } from "@/components/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface PortfolioRange {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  amc: string;
  risk_type: string;
  allocation: string;
  rebalancing: string;
  highlight_badge: string;
  slug: string;
  sort_order: number;
  status: string;
}

interface PortfolioFeature {
  id: string;
  feature_text: string;
  sort_order: number;
  range_id: string;
}

const Portfolios = () => {
  const [portfolioRanges, setPortfolioRanges] = useState<PortfolioRange[]>([]);
  const [features, setFeatures] = useState<Record<string, PortfolioFeature[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    
    const fetchPortfolios = async () => {
      if (!isMounted) return;
      setLoading(true);
      
      try {
        // Fetch portfolio ranges
        const { data: ranges, error: rangesError } = await supabase
          .from('portfolio_ranges')
          .select('*')
          .eq('status', 'active')
          .order('sort_order', { ascending: true });

        if (rangesError) throw rangesError;

        if (ranges && isMounted) {
          setPortfolioRanges(ranges);

          // Fetch features for all ranges
          const { data: portfolioFeatures, error: featuresError } = await supabase
            .from('portfolio_features')
            .select('*')
            .in('range_id', ranges.map(r => r.id))
            .order('sort_order', { ascending: true });

          if (featuresError) throw featuresError;

          // Group features by range_id
          const featuresMap: Record<string, PortfolioFeature[]> = {};
          if (portfolioFeatures) {
            portfolioFeatures.forEach(feature => {
              if (!featuresMap[feature.range_id]) {
                featuresMap[feature.range_id] = [];
              }
              featuresMap[feature.range_id].push(feature);
            });
          }
          if (isMounted) {
            setFeatures(featuresMap);
          }
        }
      } catch (error) {
        console.error('Error fetching portfolio data:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPortfolios();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const getBadgeVariant = (highlight: string) => {
    switch (highlight.toLowerCase()) {
      case 'premium': return 'default';
      case 'value': return 'secondary';
      case 'income': return 'destructive';
      default: return 'outline';
    }
  };

  const getPortfolioLink = (slug: string) => {
    // Map slug to correct route
    const routeMap: Record<string, string> = {
      'p': '/portfolios/p',
      'r-range': '/portfolios/r', 
      'dist-range': '/portfolios/dist',
      'integrity-range': '/portfolios/integrity'
    };
    return routeMap[slug] || `/portfolios/${slug}`;
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading portfolios...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <SEO
        title="Model Portfolios — P, R, Distribution & Integrity Ranges"
        description="Explore CBAM's risk-targeted Model Portfolio ranges (P1–P5, R1–R5, Distribution and Integrity) — actively managed, FCA-regulated portfolios for UK advisers."
        path="/portfolios"
      />
      {/* Header */}
      <section className="bg-gradient-to-br from-primary to-accent text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">MPS Ranges</h1>
            <p className="text-xl text-primary-foreground/90 max-w-3xl mx-auto">
              Professional model portfolio services designed to help you achieve your financial goals 
              with confidence and peace of mind.
            </p>
          </div>
        </div>
      </section>

      {/* Model Portfolios */}
      <section className="py-16 bg-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Model Portfolios</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Choose from our range of professionally managed model portfolios, each tailored 
              to different risk profiles and investment objectives.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {portfolioRanges.map((portfolio) => (
              <Card key={portfolio.id} className="relative">
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <CardTitle className="text-2xl">{portfolio.name}</CardTitle>
                      <p className="text-sm text-muted-foreground font-medium">{portfolio.subtitle}</p>
                    </div>
                    <Badge variant={getBadgeVariant(portfolio.highlight_badge)}>
                      {portfolio.highlight_badge}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-2xl font-bold text-primary">
                      {portfolio.amc}
                      <span className="text-sm text-muted-foreground font-normal"> AMC p.a.</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {portfolio.rebalancing} rebalancing
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base mb-4">
                    {portfolio.description}
                  </CardDescription>
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2">Asset Allocation:</h4>
                    <p className="text-sm text-muted-foreground">{portfolio.allocation}</p>
                  </div>
                  {features[portfolio.id] && features[portfolio.id].length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-semibold mb-2">Key Features:</h4>
                      <ul className="grid grid-cols-1 gap-1 text-sm text-muted-foreground">
                        {features[portfolio.id].map((feature) => (
                          <li key={feature.id} className="flex items-center">
                            <div className="w-1.5 h-1.5 bg-primary rounded-full mr-2 flex-shrink-0" />
                            {feature.feature_text}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                   <Button className="w-full" asChild>
                      <Link to={getPortfolioLink(portfolio.slug)}>
                        {portfolio.slug.includes("integrity") ? "Coming Soon" : "View Range Details"}
                      </Link>
                    </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Discuss Your Investment Strategy?
          </h2>
          <p className="text-xl mb-8 text-primary-foreground/90 max-w-2xl mx-auto">
            Our team of qualified investment professionals is ready to help you choose 
            the right portfolio for your financial goals.
          </p>
          <Button size="lg" variant="secondary" asChild>
            <Link to="/contact">Book Your Free Consultation</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Portfolios;